import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid
} from '@mui/material';
import { Upload, Delete, PictureAsPdf } from '@mui/icons-material';
import { useScenariosUpdateScenarioPdf, useScenariosGetScenarioPdf, useScenariosRemoveScenarioPdf } from '@/api-gen/api.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nScenarioEditorNs, I18nCommonNs } from '@/i18n/dictionaries/interfaces.ts';
import { createTranslationField } from 'app/utils/i18nUtils.js';
import { PDFOverlay } from '@/features/scenarioPdf';
import { useSignal } from '@preact/signals-react';
import { useDispatch } from 'react-redux';
import { updateScenarioPartial } from 'app/actions/scenarios/scenarioAction.ts';

interface PdfUploadComponentProps {
  scenarioId?: number;
  hasPdf?: boolean;
}

export const PdfUploadComponent: React.FC<PdfUploadComponentProps> = ({ scenarioId, hasPdf }) => {
  // Translation setup
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
  const [translateCommon] = useTranslation([I18nNamespace.Common]);
  const i18n = createTranslationField<I18nScenarioEditorNs>();
  const i18nCommon = createTranslationField<I18nCommonNs>();

  // State for PDF upload
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [showUploadSuccess, setShowUploadSuccess] = useState(false);
  const [showRemoveSuccess, setShowRemoveSuccess] = useState(false);
  const queryClient = useQueryClient();
  const pdfModalOpen = useSignal(false);
  const dispatch = useDispatch();

  // Query to get existing PDF information
  const { data: existingPdf, isLoading: isPdfLoading, refetch: refetchPdf } = useScenariosGetScenarioPdf(
    scenarioId || 0,
    {
      query: {
        enabled: !!scenarioId && !!hasPdf,
        retry: false, // Don't retry if PDF doesn't exist
        refetchOnWindowFocus: false,
        // Treat 404 as normal "no PDF exists" state, not an error
        onError: (error: any) => {
          // Only log non-404 errors
          if (error?.response?.status !== 404) {
            console.error('Error fetching PDF:', error);
          }
        }
      }
    }
  );

  // PDF upload mutation using generated API client
  const pdfUploadMutation = useScenariosUpdateScenarioPdf({
    mutation: {
      onSuccess: () => {
        setSelectedPdfFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setShowUploadSuccess(true);
        setShowRemoveSuccess(false);
        // Reset remove mutation state to allow PDF to show again
        pdfRemoveMutation.reset();
        // Refetch PDF data to update the UI
        refetchPdf();
        // Update Redux store to reflect that scenario now has PDF
        if (scenarioId) {
          dispatch(updateScenarioPartial({ scenarioId, updates: { hasPdf: true } }));
        }
      }
    }
  });

  // PDF removal mutation
  const pdfRemoveMutation = useScenariosRemoveScenarioPdf({
    mutation: {
      onSuccess: () => {
        setShowRemoveSuccess(true);
        setShowUploadSuccess(false);
        // Clear the specific PDF query data to immediately hide the UI
        queryClient.setQueryData(['scenariosGetScenarioPdf', scenarioId || 0], undefined);
        // Also invalidate scenario queries to update hasPdf flag
        queryClient.invalidateQueries({ 
          queryKey: ['scenarios'] 
        });
        // Update Redux store to reflect that scenario no longer has PDF
        if (scenarioId) {
          dispatch(updateScenarioPartial({ scenarioId, updates: { hasPdf: false } }));
        }
      }
    }
  });

  // Handle PDF file selection
  const handlePdfFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setSelectedPdfFile(file);
    }
  };

  // Handle PDF upload
  const handlePdfUpload = () => {
    if (!selectedPdfFile || !scenarioId) return;

    const formData = new FormData();
    formData.append('pdfFile', selectedPdfFile);

    pdfUploadMutation.mutate({
      id: scenarioId,
      data: formData as any // Cast to any to bypass TypeScript type checking for FormData
    });
  };

  // Handle upload button click
  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  // Handle PDF removal
  const handlePdfRemove = () => {
    if (!scenarioId) return;
    
    pdfRemoveMutation.mutate({
      id: scenarioId
    });
  };

  // Handle PDF view
  const handlePdfView = () => {
    const pdfUrl = existingPdf?.data?.url;
    
    if (pdfUrl) {
      pdfModalOpen.value = true;
    }
  };

  // Clear success messages after 3 seconds
  useEffect(() => {
    if (showUploadSuccess) {
      const timer = setTimeout(() => setShowUploadSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showUploadSuccess]);

  useEffect(() => {
    if (showRemoveSuccess) {
      const timer = setTimeout(() => setShowRemoveSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showRemoveSuccess]);

  return (
    <Grid container sx={{ mt: 3 }}>
      <Grid item xs={12}>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {translate(i18n.trainingMaterial.title)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {translate(i18n.trainingMaterial.description)}
          </Typography>
          
          <input
            type="file"
            accept=".pdf"
            onChange={handlePdfFileSelect}
            ref={fileInputRef}
            style={{ display: 'none' }}
          />
          
          {/* Show existing PDF if available */}
          {existingPdf && !isPdfLoading && !pdfRemoveMutation.isSuccess && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PictureAsPdf color="error" />
                <Typography variant="body2" fontWeight="medium">
                  {translate(i18n.trainingMaterial.currentDocument)}: {existingPdf?.data?.fileName || translate(i18n.trainingMaterial.currentDocument)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handlePdfView}
                >
                  {translate(i18n.trainingMaterial.viewDocument)}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={handlePdfRemove}
                  disabled={pdfRemoveMutation.isLoading}
                >
                  {pdfRemoveMutation.isLoading ? `${translateCommon(i18nCommon.buttonLabels.remove)}...` : translateCommon(i18nCommon.buttonLabels.remove)}
                </Button>
              </Box>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={handleUploadButtonClick}
            >
              {existingPdf ? translate(i18n.trainingMaterial.replaceDocument) : translate(i18n.trainingMaterial.selectDocument)}
            </Button>
            
            {selectedPdfFile && (
              <>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {selectedPdfFile.name}
                </Typography>
                <Button
                  variant="contained"
                  onClick={handlePdfUpload}
                  disabled={pdfUploadMutation.isLoading}
                >
                  {pdfUploadMutation.isLoading ? 'Uploading...' : 'Upload'}
                </Button>
              </>
            )}
          </Box>
          
          {pdfUploadMutation.isError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {translate(i18n.trainingMaterial.uploadFailed)}
            </Typography>
          )}
          
          {showUploadSuccess && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              {translate(i18n.trainingMaterial.uploadSuccess)}
            </Typography>
          )}
          
          {pdfRemoveMutation.isError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {translate(i18n.trainingMaterial.removeFailed)}
            </Typography>
          )}
          
          {showRemoveSuccess && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              {translate(i18n.trainingMaterial.removeSuccess)}
            </Typography>
          )}
        </Box>
      </Grid>
      <PDFOverlay
        open={pdfModalOpen.value}
        pdfUrl={existingPdf?.data?.url || null}
        onClose={() => pdfModalOpen.value = false}
      />
    </Grid>
  );
};